import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../creds';
import { FaEdit, FaEye, FaTrash } from 'react-icons/fa';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import { toast } from 'react-toastify';
import AdForm from './AdForm';
import ShowPreview from './showPreview';
import Username from '../Username';
import { formatPhoneNumber } from '../NumberFormatter';
import MatrimonialClassifiedsPDF from './MatrimonialClassifiedsPDF';
import { TfiClose } from 'react-icons/tfi';
import PaginationClassified from './PaginationClassified'
import New from '../common/New';


const CRNSchemaModal = ({ isOpen, onClose, item }) => {
  if (!isOpen || !item) return null;
  const schemaData = Object.entries(item)
    .filter(([key, value]) => value != null && key !== 'id')
    .map(([key, value]) => ({
      key: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
      value: String(value).trim() || '-'
    }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">CRN Schema Details</h3>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
            title="Close"
          >
            <TfiClose className='text-gray-900 font-extrabold' size={28} />
          </button>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left text-xl font-extrabold text-gray-900">Field</th>
              <th className="border border-gray-300 px-4 py-2 text-left text-xl font-extrabold text-gray-900">Value</th>
            </tr>
          </thead>
          <tbody>
            {schemaData.map(({ key, value }, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-4 py-2 text-lg text-gray-900 font-bold">{key}</td>
                <td className="border border-gray-300 px-4 py-2 text-lg text-gray-900 break-words">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md shadow"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Table Component
const ClassifiedTable = ({ data, onSort, sortConfig, onView, onEdit, onDelete, currentPage, perPage, onCRNClick }) => {
  const headers = [
    { key: 'index', label: 'SN', width: 'w-[4%]', minWidth: 'min-w-[20px]' },
    { key: 'crn', label: 'CRN', width: 'w-[12%]', minWidth: 'min-w-[80px]' },
    { key: 'gender', label: 'Gender', width: 'w-[8%]', minWidth: 'min-w-[80px]' },
    { key: 'category', label: 'Category', width: 'w-[9%]', minWidth: 'min-w-[80px]' },
    { key: 'subcategory', label: 'Subcategory', width: 'w-[11%]', minWidth: 'min-w-[90px]' },
    { key: 'hline1', label: 'Heading', width: 'w-[12%]', minWidth: 'min-w-[90px]' },
    { key: 'mobile', label: 'Contact', width: 'w-[10%]', minWidth: 'min-w-[80px]' },
    { key: 'agency', label: 'Agency', width: 'w-[8%]', minWidth: 'min-w-[60px]' },
    { key: 'actions', label: 'Actions', width: 'w-[10%]', minWidth: 'min-w-[100px]' },
  ];

  const getSortIcon = (key) => {
    return sortConfig.direction === 'asc' ? (
      <AiOutlineArrowUp className="inline text-gray-900" />
    ) : (
      <AiOutlineArrowDown className="inline text-gray-900" />
    );
  };

  const combineHlines = (item) => {
    const parts = [item.hline1, item.hline2, item.hline3].filter(Boolean);
    return parts.length > 0 ? parts.join('\n') : '-';
  };

  return (
    <div className="w-full overflow-x-hidden">
      {/* Table for larger screens */}
      <div className="hidden md:block">
        <div className="mx-auto">
          <table className="w-full bg-white border border-gray-300 shadow-md rounded-lg table-fixed">
            <thead className=''>
              <tr className="bg-gray-100 text-gray-900 text-left font-bold">
                {headers.map(({ key, label, width }) => (
                  <th
                    key={key}
                    className={`
                                     px-2 py-2 text-[12px] 
                                     whitespace-nowrap sm:whitespace-pre-wrap sm:break-words
                                     border border-gray-300
                                     ${key !== 'index' && key !== 'actions' ? 'cursor-pointer hover:bg-gray-200' : ''}
                                     ${width}
                                   `}
                    onClick={() => key !== 'actions' && key !== 'index' && onSort(key)}
                  >
                    {label}
                    {key !== 'actions' && key !== 'index' && getSortIcon(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-2 text-[12px] border border-gray-300 break-words">
                      {(currentPage - 1) * perPage + index + 1}
                    </td>
                    <td
                      className="py-2 px-2 text-[12px] border border-gray-300 break-words cursor-pointer hover:text-blue-600"
                      onClick={() => onCRNClick(item)}
                    >
                      {item.crn
                        ? String(item.crn).slice(0, 4) + "-" +
                        String(item.crn).slice(4, 8) + "-" +
                        String(item.crn).slice(8, 12) + "-" +
                        String(item.crn).slice(12)
                        : '-'}
                    </td>
                    <td className="py-2 px-2 text-[12px] border border-gray-300 break-words">
                      {item.gender || '-'}
                    </td>
                    <td className="py-2 px-2 text-[12px] border border-gray-300 break-words">
                      {item.category || '-'}
                    </td>
                    <td className="py-2 px-2 text-[12px] border border-gray-300 break-words">
                      {item.subcategory || '-'}
                    </td>
                    <td className="py-2 px-2 text-[12px] border border-gray-300 break-words">
                      {combineHlines(item)}
                    </td>
                    <td className="py-2 px-2 text-xs sm:text-[12px] border border-gray-300 break-words">
                      {formatPhoneNumber(item.mobile || item.whatsapp || item.email || '-')}
                    </td>
                    <td className="py-2 px-2 text-[12px] border border-gray-300">
                      {item.agency || '-'}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button
                          onClick={() => onView(item)}
                          className="text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 p-1 rounded"
                          title="View"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => onEdit(item)}
                          className="bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-black p-1 rounded"
                          title="Edit"
                        >
                          <FaEdit size={18} />
                        </button>
                        <div className="w-8">
                          <MatrimonialClassifiedsPDF data={item} />
                        </div>
                        <button
                          onClick={() => onDelete(item.id)}
                          className="text-white bg-red-600 hover:bg-red-700 border border-red-600 p-1 rounded"
                          title="Delete"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className="py-6 px-4 text-center text-gray-500 text-[clamp(12px,1.5vw,14px)]"
                    colSpan={headers.length}
                  >
                    No classifieds uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accordion View for mobile screens */}
      <div className="md:hidden flex flex-col gap-4 p-2">
        {data.length > 0 ? (
          data.map((item, index) => (
            <details key={item.id} className="bg-white border border-gray-300 rounded-lg shadow-md p-3">
              <summary className="font-semibold text-gray-700 cursor-pointer list-none text-[clamp(12px,2.5vw,14px)] truncate">
                S.No.: {(currentPage - 1) * perPage + index + 1} - {item.category || '-'}
              </summary>
              <div className="mt-2 grid grid-cols-1 gap-2 text-gray-600 text-[clamp(11px,2.2vw,13px)]">
                <div className="truncate">
                  <span className="font-semibold">CRN No.:</span>{' '}
                  <span
                    className="cursor-pointer hover:text-blue-600"
                    onClick={() => onCRNClick(item)}
                  >
                    {item.crn || '-'}
                  </span>
                </div>
                <div className="truncate"><span className="font-semibold">Gender:</span> {item.gender || '-'}</div>
                <div className="truncate"><span className="font-semibold">Subcategory:</span> {item.subcategory || '-'}</div>
                <div className="truncate"><span className="font-semibold">Line 1:</span> {item.hline1 || '-'}</div>
                <div className="truncate"><span className="font-semibold">Contact:</span> {formatPhoneNumber(item.mobile || item.whatsapp || item.email || '-')}</div>
                <div className="truncate"><span className="font-semibold">Agency:</span> {item.agency || '-'}</div>
                <div className="flex flex-col space-y-2 mt-2">
                  <button
                    onClick={() => onView(item)}
                    className="text-white bg-blue-600 hover:bg-blue-700 border-blue-600 p-2 rounded"
                    title="View"
                  >
                    <FaEye size={16} className="mx-auto" />
                  </button>
                  <button
                    onClick={() => onEdit(item)}
                    className="bg-yellow-500 hover:bg-yellow-600 border-yellow-500 text-black p-2 rounded"
                    title="Edit"
                  >
                    <FaEdit size={16} className="mx-auto" />
                  </button>
                  <MatrimonialClassifiedsPDF data={item} />
                  <button
                    onClick={() => onDelete(item.id)}
                    className="text-white bg-red-600 hover:bg-red-700 p-2 rounded"
                    title="Delete"
                  >
                    <FaTrash size={16} className="mx-auto" />
                  </button>
                </div>
              </div>
            </details>
          ))
        ) : (
          <div className="text-center text-gray-500 py-6 text-[clamp(12px,2.5vw,14px)]">
            No classifieds uploaded yet.
          </div>
        )}
      </div>
    </div>
  );
};


const DeleteConfirmModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
        <p className="text-gray-600 mb-6">Are you sure you want to delete this classified? This action cannot be undone.</p>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md shadow"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md shadow"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};


// Main Classifieds Component
const View = ({ sidebarOpen }) => {
    // eslint-disable-next-line
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [classifieds, setClassifieds] = useState([]);
  const [filteredClassifieds, setFilteredClassifieds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isCRNSchemaModalOpen, setIsCRNSchemaModalOpen] = useState(false);
  const [selectedCRNItem, setSelectedCRNItem] = useState(null);


  const fetchClassifieds = async () => {
    setIsLoading(true);
    setError(null);
  
    try {
      const response = await axios.get(`${API_URL}/classifiedsPages`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        params: {
          page: currentPage,
          per_page: perPage,
          search: searchTerm || undefined,
        },
      });
  
      setFilteredClassifieds(response.data.data);
      setTotal(response.data.total);
  
    } catch (err) {
      console.error("Fetch error:", err); 
      toast.error(err.response?.data?.detail || 'Failed to fetch classifieds');  
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchClassifieds();
    // eslint-disable-next-line
  }, [currentPage, perPage]);


const handleSearch = async () => {
  const loadingId = toast.loading("Please Wait...");
  if (!searchTerm.trim()) {
    setFilteredClassifieds([...classifieds]);
    setCurrentPage(1);
    return;
  }

  try {
    const response = await axios.post(`${API_URL}/search`, {
      search: searchTerm.toLowerCase(),
    });

    setFilteredClassifieds(response.data.data);
    setCurrentPage(1);
    setTotal(response.data.total);
    toast.dismiss(loadingId);
    toast.success(`Data Fetching successfully....`)
  } catch (error) {
    console.error('Error fetching search results:', error);
    toast.error(`Error: ${error}`)
  }
};


  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });

    setFilteredClassifieds((prev) => {
      if (!Array.isArray(prev)) return [];
      const sorted = [...prev].sort((a, b) => {
        if (key === 'index') {
          return sortConfig.direction === 'asc' ? a.index - b.index : b.index - a.index;
        }

        const aValue = a[key] ?? '';  // null/undefined safe
        const bValue = b[key] ?? '';

        // If numbers → compare numerically
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Fallback: string comparison
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
      return sorted;
    });
  };


  const handleView = (item) => {
    setFormData(item);
    setShowPreview(true);
  };

  const handleEdit = (item) => {
    setEditData(item);
    setShowForm(true);
  };
  const formRef = useRef(null);
  useEffect(() => {
    if (editData && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editData]);

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleCRNClick = (item) => {
    setSelectedCRNItem(item);
    setIsCRNSchemaModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
    // eslint-disable-next-line
      const response = await axios.delete(`${API_URL}/classifieds/${deleteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setIsDeleteModalOpen(false);
      setDeleteId(null);
      fetchClassifieds();
      toast.success('Classified deleted successfully');
    } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete classified');
        setIsDeleteModalOpen(false);
        setDeleteId(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteId(null);
  };

  const handleCloseCRNSchemaModal = () => {
    setIsCRNSchemaModalOpen(false);
    setSelectedCRNItem(null);
  };

  const handleAddNew = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  
  return (
    <>
      <Username />
      {showForm && (
        <AdForm
          setSubmissions={(submissions) => {
            localStorage.setItem('submissions', JSON.stringify(submissions));
            fetchClassifieds();
          }}
          setShowForm={setShowForm}
          editData={editData}
          formRef={formRef}
        />
      )}

      <div className="w-full p-2 sm:p-6">
        <div className="flex flex-col items-center sm:flex-row sm:justify-between mb-2 gap-4 w-full">
          <New handleAddNew={handleAddNew} />
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto max-w-xs sm:max-w-none">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search classifieds..."
              className="border rounded-md px-3 py-2 w-[300px] h-12 text-[clamp(12px,2vw,14px)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="bg-blue-600 text-white hover:bg-blue-700 font-medium py-2 px-4 h-12 rounded-lg text-[clamp(12px,2vw,14px)] w-full sm:w-auto"
            >
              Search
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md text-[clamp(12px,2vw,14px)]">
            {error}
          </div>
        )}
        {/* <div className="flex justify-end py-2">
          <button className="p-2 rounded text-white text-3xl">
            <MatrimonialClassifiedsPDF title="Print Classified Preview" data={filteredClassifieds} />
          </button>
        </div> */}

        {isLoading ? (
          <div className="text-center text-gray-500 py-6 text-[clamp(12px,2vw,14px)]">
            Loading...
          </div>
        ) : (
          <>
            <div className={sidebarOpen ? 'max-w-[calc(100vw-250px)]' : 'max-w-full'}>
              <ClassifiedTable
                data={filteredClassifieds}
                onSort={handleSort}
                sortConfig={sortConfig}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                currentPage={currentPage}
                perPage={perPage}
                onCRNClick={handleCRNClick}
              />
            </div>
            <PaginationClassified
              total={total}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
            <ShowPreview
              showPreview={showPreview}
              setShowPreview={setShowPreview}
              formData={formData}
              title="View details"
              flag={true}
            />
          </>
        )}

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
        />
        <CRNSchemaModal
          isOpen={isCRNSchemaModalOpen}
          onClose={handleCloseCRNSchemaModal}
          item={selectedCRNItem}
        />
      </div>
    </>
  );
};

export default View;